/**
 * Resolve visibilidade condicional de um campo a partir das respostas
 * atuais da submissão. Ver `FormField.visible_if` em `schemas.ts`.
 *
 * **3 operadores suportados**:
 *   - `equals: 'sim'`     → visível quando o valor for exatamente "sim"
 *   - `includes: 'outro'` → visível quando o valor for um array que contém "outro"
 *                           (para multiselect futuro)
 *   - `not_empty: true`   → visível quando o valor for não-vazio (string com
 *                           length > 0, ou array com length > 0)
 *
 * Se o campo-alvo não existe nas respostas ou seu valor é undefined/null,
 * a condição é considerada **false** — campo fica escondido. Isso evita
 * flicker no load inicial antes do prefill chegar.
 */
import type { FormVisibleIf } from '../../schemas.js';

export function isVisible(
  visibleIf: FormVisibleIf | undefined,
  answers: Record<string, unknown>,
): boolean {
  if (!visibleIf) return true;

  const targetValue = answers[visibleIf.field];

  if (visibleIf.equals !== undefined) {
    return targetValue === visibleIf.equals;
  }

  if (visibleIf.includes !== undefined) {
    if (!Array.isArray(targetValue)) return false;
    return (targetValue as unknown[]).includes(visibleIf.includes);
  }

  if (visibleIf.not_empty === true) {
    if (targetValue == null) return false;
    if (typeof targetValue === 'string') return targetValue.length > 0;
    if (Array.isArray(targetValue)) return targetValue.length > 0;
    return true;
  }

  // Se caiu aqui, visible_if está malformado (schema já deve ter
  // barrado no parse do template, mas defensive coding paga).
  return true;
}
